import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, ListFilter, Play, Trash2, RefreshCw, Power, Clock, ChevronDown, ChevronUp, Wand2, Pencil, Users, Film, Tv, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard, PageHeader, Spinner, Modal } from "../components/common";
import { DomainGate } from "../components/DomainGate";

const WEEKDAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const schedText = (s) => `${s.freq === "weekly" ? `Semanal · ${WEEKDAYS[s.weekday ?? 0]}` : "Diário"} · ${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")}`;
const nonEmptyMap = (m, cats) => Object.fromEntries(Object.entries(m || {}).filter(([k, v]) => v && (!cats || cats.includes(k))));

function MapRow({ c, kind, mapping, setMapping, folders, prefix, onRemove }) {
  const val = mapping[c] || "";
  const isNew = !!val && !folders.includes(val);
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-800/60" data-testid={`${prefix}-map-${c}`}>
      {onRemove && (
        <button type="button" data-testid={`${prefix}-remove-${c}`} title="Remover esta categoria da lista (não importa)" onClick={() => onRemove(c)}
          className="p-1 rounded-md text-slate-600 hover:text-rose-300 hover:bg-rose-400/10 transition-colors shrink-0"><X size={12} /></button>
      )}
      {kind && (
        <span className={`inline-flex items-center gap-1 text-[8px] font-mono2 uppercase px-1.5 py-0.5 rounded border shrink-0 ${kind.cls}`} data-testid={`${prefix}-kind-${c}`} title={kind.txt}>
          <kind.icon size={10} /> {kind.txt}
        </span>
      )}
      <span className="text-[11px] text-slate-300 flex-1 min-w-0 truncate" title={c}>{c}</span>
      {isNew && <span className="text-[8px] font-mono2 uppercase px-1 py-0.5 rounded bg-amber-400/10 text-amber-300 border border-amber-400/25 shrink-0" data-testid={`${prefix}-new-${c}`}>nova</span>}
      <button type="button" data-testid={`${prefix}-usecat-${c}`} title={`Criar pasta nova "${c}" (nome da categoria)`}
        onClick={() => setMapping({ ...mapping, [c]: c })}
        className="text-[9px] font-mono2 px-1.5 py-1 rounded-md btn-ghost-dark text-emerald-300/90 hover:text-emerald-200 shrink-0">+ criar</button>
      <select data-testid={`${prefix}-mapsel-${c}`} value={folders.includes(val) ? val : ""}
        onChange={(e) => { if (e.target.value) setMapping({ ...mapping, [c]: e.target.value }); }}
        title="Todas as tuas pastas — escolhe uma para trocar"
        className="input-dark rounded-lg px-2 py-1.5 text-[11px] w-44 max-w-[40%] shrink-0 cursor-pointer">
        <option value="">{isNew ? `✏️ ${val} (nova)` : "— escolher pasta —"}</option>
        {folders.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
      <input data-testid={`${prefix}-maptxt-${c}`} value={val}
        onChange={(e) => setMapping({ ...mapping, [c]: e.target.value })}
        placeholder="ou escreve pasta nova" title="Texto livre — o que escreveres fica guardado como pasta nova"
        className="input-dark rounded-lg px-2 py-1.5 text-[11px] w-36 max-w-[30%] shrink-0" />
    </div>
  );
}

function MapTable({ cats, mapping, setMapping, folders, prefix, kinds, onRemove, folderKinds }) {
  const kindOf = (c) => {
    const k = kinds?.[c];
    if (k === "tv") return { icon: Tv, txt: "Série", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" };
    if (k === "movie") return { icon: Film, txt: "Filme", cls: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10" };
    return null;
  };
  // divisão Filmes / Séries — pasta é de séries se o nome diz "séries" OU se o conteúdo dela é maioritariamente séries
  const isSeriesFolder = (f) => /s[ée]ries/i.test(f) || ((folderKinds?.[f]?.series || 0) > 0 && (folderKinds[f].series || 0) >= (folderKinds[f].movies || 0));
  const seriesFolders = folders.filter(isSeriesFolder);
  const movieFolders = folders.filter((f) => !isSeriesFolder(f));
  // divisão Filmes / Séries — cada secção mostra só as suas pastas no dropdown
  const movies = cats.filter((c) => kindOf(c)?.txt === "Filme");
  const series = cats.filter((c) => kindOf(c)?.txt === "Série");
  const others = cats.filter((c) => !kindOf(c));
  const Section = ({ title, Icon, cls, list, flist }) => list.length > 0 && (
    <div>
      <p className={`text-[10px] font-mono2 uppercase tracking-widest px-1 pb-1.5 flex items-center gap-1.5 ${cls}`} data-testid={`${prefix}-section-${title.toLowerCase()}`}>
        <Icon size={11} /> {title} <span className="text-slate-600">({list.length})</span>
      </p>
      <div className="space-y-1">
        {list.map((c) => (
          <MapRow key={c} c={c} kind={kindOf(c)} mapping={mapping} setMapping={setMapping}
            folders={flist.length ? flist : folders} prefix={prefix} onRemove={onRemove} />
        ))}
      </div>
    </div>
  );
  return (
    <div className="max-h-[36vh] overflow-y-auto space-y-4 pr-1" data-testid={`${prefix}-map-table`}>
      <Section title="Filmes" Icon={Film} cls="text-cyan-300" list={movies} flist={movieFolders} />
      <Section title="Séries" Icon={Tv} cls="text-amber-300" list={series} flist={seriesFolders} />
      <Section title="Outras" Icon={ListFilter} cls="text-slate-400" list={others} flist={folders} />
    </div>
  );
}

export default function Agendadas() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [url, setUrl] = useState("");
  const [savedLists, setSavedLists] = useState([]); // listas M3U já lidas (dropdown de reutilização)
  const [savedSel, setSavedSel] = useState(""); // lista guardada atualmente selecionada no dropdown

  const deleteSavedList = async () => {
    if (!savedSel) return;
    try {
      await api.delete("/import/saved-lists", { params: { url: savedSel } });
      setSavedLists((prev) => prev.filter((l) => l.url !== savedSel));
      setSavedSel("");
      toast.success("Lista guardada apagada");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { api.get("/import/saved-lists").then(({ data }) => setSavedLists(Array.isArray(data) ? data : [])).catch(() => {}); }, []);
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null); // {total, categories}
  const [sel, setSel] = useState({});
  const [kindSel, setKindSel] = useState({});  // override manual: categoria → "movie" | "tv"
  const [folderKinds, setFolderKinds] = useState({});  // pasta → {movies, series} (da árvore) — divide corretamente pastas de filmes/séries
  const [scope, setScope] = useState("movie");  // âmbito da importação: "movie" | "tv" | "all" — canais (live) nunca entram
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [schedules, setSchedules] = useState(null);
  const [schedOpen, setSchedOpen] = useState(false);
  const [form, setForm] = useState({ name: "", freq: "daily", weekday: 0, time: "03:00", dest: "" });
  const [histOpen, setHistOpen] = useState({});
  const [folders, setFolders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [mapStats, setMapStats] = useState(null); // {matched, new, total}
  const [edit, setEdit] = useState(null); // schedule em edição
  const [editForm, setEditForm] = useState(null); // {name, freq, weekday, time, dest, mapping}

  const qs = useCallback((extra = "") => `${extra ? extra + "&" : "?"}${accountId ? `user_id=${accountId}` : ""}`.replace(/[?&]$/, ""), [accountId]);

  const load = useCallback(() => {
    api.get(`/scheduled-imports${qs()}`).then((r) => setSchedules(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  }, [qs]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setFolders([]);
    api.get(`/folders/tree${qs()}`).then((r) => {
      const fs = r.data.folders || [];
      setFolders(fs.map((f) => f.name));
      setFolderKinds(Object.fromEntries(fs.map((f) => [f.name, f])));
    }).catch(() => {});
  }, [qs]);
  useEffect(() => {
    if (isAdmin) {
      api.get("/folders/tree").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
    }
  }, [isAdmin]);
  useEffect(() => {
    if (!(schedules || []).some((s) => s.running)) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [schedules, load]);

  const analyze = async () => {
    if (!url.trim()) return;
    setAnalyzing(true);
    setAnalysis(null);
    setSel({});
    setMapping({});
    setMapStats(null);
    setSchedOpen(false);
    try {
      const { data } = await api.post("/import/m3u-analyze", { url: url.trim() });
      setAnalysis(data);
      toast.success(`Lista lida: ${data.total.toLocaleString("pt-PT")} itens em ${data.categories.length} categorias — escolha e mapeie`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setAnalyzing(false); }
  };

  const selGroups = Object.keys(sel).filter((k) => sel[k]);
  const detectedKind = (c) => ((c.episodes ?? 0) > 0 && (c.episodes ?? 0) >= c.movie ? "tv" : "movie");
  const effKind = (c) => (scope !== "all" ? scope : kindSel[c.name] || detectedKind(c));
  const effKinds = () => Object.fromEntries((analysis?.categories || []).map((c) => [c.name, effKind(c)]));
  const selItems = analysis ? analysis.categories.filter((c) => sel[c.name])
    .reduce((a, c) => a + (scope === "movie" ? c.movie : scope === "tv" ? (c.episodes ?? c.series) : c.movie + (c.episodes ?? c.series)), 0) : 0;
  const filtered = analysis ? analysis.categories.filter((c) =>
    (!q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase())) &&
    (scope === "all" || (scope === "movie" ? c.movie > 0 : c.series > 0))) : [];
  const mappedCount = selGroups.filter((g) => (mapping[g] || "").trim()).length;

  const autoMap = async (cats, kinds, target) => {
    if (!cats.length) return null;
    setBusy(target === "edit" ? "map-edit" : "map");
    try {
      const { data } = await api.post(`/scheduled-imports/auto-map${qs()}`, { categories: cats, kinds });
      if (data.folders?.length) setFolders((f) => [...new Set([...f, ...data.folders])].sort());
      if (target === "edit") {
        setEditForm((f) => ({ ...f, mapping: { ...(f.mapping || {}), ...data.mapping } }));
      } else {
        setMapping((m) => ({ ...m, ...data.mapping }));
        setMapStats({ matched: data.matched, new: data.new, total: data.total });
      }
      toast.success(`Auto-mapeado: ${data.matched} ligadas às suas pastas · ${data.new} com pasta nova proposta — verifique antes de salvar`);
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; } finally { setBusy(""); }
  };

  const autoMapSelected = () => {
    const kinds = {};
    (analysis?.categories || []).forEach((c) => { if (sel[c.name]) kinds[c.name] = effKind(c); });
    autoMap(selGroups, kinds, "create");
  };

  const importNow = async () => {
    if (!selGroups.length) return;
    setBusy("now");
    try {
      await api.post(`/import/m3u-url${qs()}`, { url: url.trim(), kind_filter: scope === "tv" ? "series" : scope, groups: selGroups, mapping: nonEmptyMap(mapping, selGroups) });
      toast.success(`Importação de ${selGroups.length} categoria(s) iniciada (${mappedCount} mapeadas) — acompanhe na Fila`);
      setAnalysis(null);
      setSel({});
      setMapping({});
      setMapStats(null);
      setUrl("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const createSchedule = async () => {
    if (!selGroups.length) return;
    const [hour, minute] = form.time.split(":").map(Number);
    setBusy("sched");
    try {
      await api.post(`/scheduled-imports${qs()}`, {
        url: url.trim(), groups: selGroups, name: form.name.trim() || null, dest_folder: form.dest || null,
        mapping: nonEmptyMap(mapping, selGroups),
        freq: form.freq, weekday: form.weekday, hour, minute,
        group_kinds: (analysis?.categories || []).filter((c) => sel[c.name]).map((c) => ({ name: c.name, movie: c.movie, series: c.series })),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      toast.success("Agendamento criado com o mapeamento salvo — pode editá-lo quando quiser");
      setSchedOpen(false);
      setAnalysis(null);
      setSel({});
      setMapping({});
      setMapStats(null);
      setUrl("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/scheduled-imports/${s.id}`, { active: !s.active });
      toast.success(s.active ? "Agendamento pausado" : "Agendamento ativado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runNow = async (s) => {
    setBusy(`run-${s.id}`);
    try {
      await api.post(`/scheduled-imports/${s.id}/run`);
      toast.success("A correr agora — acompanhe na Fila");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const del = async (s) => {
    setBusy(`del-${s.id}`);
    try {
      await api.delete(`/scheduled-imports/${s.id}`);
      toast.success("Agendamento apagado");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const openEdit = (s) => {
    setEdit(s);
    setEditForm({
      name: s.name, freq: s.freq, weekday: s.weekday ?? 0,
      time: `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")}`,
      dest: s.dest_folder || "", mapping: { ...(s.mapping || {}) },
    });
  };

  const saveEdit = async () => {
    if (!edit || !editForm) return;
    const [hour, minute] = editForm.time.split(":").map(Number);
    setBusy("edit");
    try {
      await api.patch(`/scheduled-imports/${edit.id}`, {
        name: editForm.name, freq: editForm.freq, weekday: editForm.weekday, hour, minute,
        dest_folder: editForm.dest || null, mapping: editForm.mapping || {},
      });
      toast.success("Agendamento atualizado — mapeamento salvo");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="agendadas-page">
      <PageHeader eyebrow="Automação" title="Importações Agendadas"
        subtitle="Leia uma lista M3U, escolha as categorias, mapeie para as suas pastas e importe já — ou agende. Por defeito corre TODOS os dias à hora que definires (tipo SYNC diário); podes mudar para semanal ao editar." />
      <DomainGate />

      <GlassCard className="p-6 space-y-4" data-testid="cat-import-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center shrink-0">
            <ListFilter size={20} className="text-amber-300" />
          </div>
          <div>
            <h3 className="font-display text-white font-semibold text-sm">Importar por categoria</h3>
            <p className="text-[11px] text-slate-500">1. cole o link da lista · 2. leia as categorias · 3. escolha, mapeie para as suas pastas e importe ou agende</p>
          </div>
        </div>
        {isAdmin && accounts && (
          <div className="flex items-center gap-2" data-testid="ag-account-row">
            <Users size={14} className="text-amber-400 shrink-0" />
            <select data-testid="ag-account-select" value={accountId} onChange={(e) => { setAccountId(e.target.value); setAnalysis(null); setSel({}); setMapping({}); setSchedules(null); }} className="input-dark rounded-lg px-3 py-2 text-sm max-w-[240px]">
              <option value="">Minha conta</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || a.email}</option>)}
            </select>
            {accountId && <span className="text-[10px] font-mono2 text-amber-300">a ler/gravar na conta do cliente selecionado</span>}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2" data-testid="import-scope-row">
          <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Importar:</span>
          {[["movie", "Só filmes", Film], ["tv", "Só séries", Tv], ["all", "Tudo", null]].map(([v, lbl, Icon]) => (
            <button key={v} type="button" data-testid={`scope-${v}`}
              onClick={() => { setScope(v); setSel({}); setMapping({}); setKindSel({}); }}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-mono2 uppercase flex items-center gap-1.5 border transition-colors ${scope === v ? "btn-neon border-transparent" : "btn-ghost-dark text-slate-400 border-slate-700 hover:border-slate-600"}`}>
              {Icon && <Icon size={12} />} {lbl}
            </button>
          ))}
          <span className="text-[10px] font-mono2 text-slate-600">canais (live) nunca entram</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {savedLists.length > 0 && (
            <div className="flex items-center gap-1.5 shrink-0">
              <select data-testid="saved-lists-select" value={savedSel} onChange={(e) => { const v = e.target.value; setSavedSel(v); if (v) { setUrl(v); setAnalysis(null); setSel({}); setMapping({}); setKindSel({}); } }}
                className="input-dark rounded-xl px-3 py-3 text-xs max-w-[190px]" title="Listas M3U que já leu antes — selecione para preencher">
                <option value="">Listas guardadas…</option>
                {savedLists.map((l) => <option key={l.url} value={l.url}>{l.name}{l.total ? ` (${l.total})` : ""}</option>)}
              </select>
              {savedSel && (
                <button type="button" data-testid="saved-list-delete" title="Apagar esta lista guardada" onClick={deleteSavedList}
                  className="p-3 rounded-xl border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors shrink-0">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          )}
          <input data-testid="cat-url-input" className="input-dark flex-1 min-w-0 rounded-xl px-4 py-3 text-sm font-mono2"
            placeholder="http://fornecedor.com/get.php?username=...&type=m3u_plus&output=mpegts"
            value={url} onChange={(e) => { setUrl(e.target.value); setAnalysis(null); setSel({}); setMapping({}); setKindSel({}); }} />
          <button data-testid="cat-analyze-btn" onClick={analyze} disabled={analyzing || !url.trim()}
            className="btn-neon px-5 py-3 rounded-xl text-sm flex items-center justify-center gap-2 shrink-0">
            {analyzing ? <Spinner /> : <><ListFilter size={15} /> Ler categorias</>}
          </button>
        </div>
        {analyzing && (
          <p className="text-xs text-slate-400 flex items-center gap-2" data-testid="cat-analyzing"><Spinner /> A ler a lista no servidor… listas grandes podem demorar ~1 minuto. Pode esperar aqui.</p>
        )}

        {analysis && !analyzing && (
          <div className="space-y-3 pt-1" data-testid="cat-picker">
            {(analysis.live_skipped || 0) > 0 && (
              <p className="text-[10px] font-mono2 text-slate-500 px-1" data-testid="live-skipped-info">{analysis.live_skipped.toLocaleString("pt-PT")} canais ignorados automaticamente (não importamos canais)</p>
            )}
            <div className="flex items-center gap-2">
              <input data-testid="cat-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar categorias…" className="input-dark flex-1 rounded-lg px-3 py-2 text-sm" />
              <button type="button" data-testid="cat-select-all" onClick={() => { const s = { ...sel }; filtered.forEach((c) => { s[c.name] = true; }); setSel(s); }} className="text-[10px] px-2.5 py-1.5 rounded-lg btn-ghost-dark text-cyan-300 shrink-0">Todas</button>
              <button type="button" data-testid="cat-select-none" onClick={() => { setSel({}); setMapping({}); }} className="text-[10px] px-2.5 py-1.5 rounded-lg btn-ghost-dark text-slate-400 shrink-0">Nenhuma</button>
            </div>
            <div className="max-h-[40vh] overflow-y-auto space-y-1 pr-1" data-testid="cat-list">
              {filtered.map((c) => (
                <div key={c.name} data-testid={`cat-row-${c.name}`} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-colors ${sel[c.name] ? "border-cyan-400/40 bg-cyan-400/5" : "border-slate-800/60 hover:border-slate-700"}`}>
                  <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                    <input type="checkbox" data-testid={`cat-check-${c.name}`} checked={!!sel[c.name]} onChange={() => setSel({ ...sel, [c.name]: !sel[c.name] })} className="accent-cyan-400 shrink-0" />
                    <span className="text-xs text-slate-200 flex-1 min-w-0 truncate">{c.name}</span>
                    <span className="text-[9px] font-mono2 text-slate-500 shrink-0">
                      {scope === "tv"
                        ? `${(c.series ?? 0).toLocaleString("pt-PT")} séries · ${(c.episodes ?? 0).toLocaleString("pt-PT")} episódios`
                        : scope === "movie"
                          ? `${(c.movie ?? 0).toLocaleString("pt-PT")} filmes`
                          : `${(c.movie ?? 0).toLocaleString("pt-PT")} filmes · ${(c.series ?? 0).toLocaleString("pt-PT")} séries · ${(c.episodes ?? 0).toLocaleString("pt-PT")} ep.`}
                    </span>
                  </label>
                  {scope === "movie" && (c.series ?? 0) > 0 && (
                    <span className="text-[8px] font-mono2 uppercase px-1.5 py-0.5 rounded border border-amber-400/30 bg-amber-400/10 text-amber-300 shrink-0"
                      title="Esta categoria também tem séries — NÃO serão importadas neste modo" data-testid={`cat-mixed-${c.name}`}>+{c.series} séries fora</span>
                  )}
                  {scope === "tv" && (c.movie ?? 0) > 0 && (
                    <span className="text-[8px] font-mono2 uppercase px-1.5 py-0.5 rounded border border-cyan-400/30 bg-cyan-400/10 text-cyan-300 shrink-0"
                      title="Esta categoria também tem filmes — NÃO serão importados neste modo" data-testid={`cat-mixed-${c.name}`}>+{c.movie} filmes fora</span>
                  )}
                  {scope === "all" ? (
                    <span className="flex rounded-lg overflow-hidden border border-slate-700 shrink-0" data-testid={`cat-kind-${c.name}`} title="Tipo: escolhe se esta categoria é de Filmes ou de Séries — define a secção e o auto-mapeamento">
                      {["movie", "tv"].map((kv) => (
                        <button key={kv} type="button" data-testid={`cat-kind-${kv}-${c.name}`}
                          onClick={() => setKindSel({ ...kindSel, [c.name]: kv })}
                          className={`px-2 py-1 text-[9px] font-mono2 uppercase flex items-center gap-1 transition-colors ${effKind(c) === kv ? (kv === "tv" ? "bg-amber-400/20 text-amber-200" : "bg-cyan-400/20 text-cyan-200") : "text-slate-600 hover:text-slate-400"} ${kindSel[c.name] === kv ? "font-bold" : ""}`}>
                          {kv === "tv" ? <Tv size={10} /> : <Film size={10} />} {kv === "tv" ? "Série" : "Filme"}
                        </button>
                      ))}
                    </span>
                  ) : (
                    <span className={`text-[8px] font-mono2 uppercase px-1.5 py-0.5 rounded border shrink-0 ${scope === "tv" ? "text-amber-300 border-amber-400/30 bg-amber-400/10" : "text-cyan-300 border-cyan-400/30 bg-cyan-400/10"}`} data-testid={`cat-kind-${c.name}`}>
                      {scope === "tv" ? "Série" : "Filme"}
                    </span>
                  )}
                </div>
              ))}
              {filtered.length === 0 && <p className="text-xs text-slate-500 text-center py-6">Nenhuma categoria corresponde ao filtro.</p>}
            </div>

            {selGroups.length > 0 && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3" data-testid="map-panel">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 flex-1">
                    Mapeamento categoria → pasta
                  </p>
                  <button data-testid="map-auto-btn" onClick={autoMapSelected} disabled={!!busy}
                    className="btn-neon px-3 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5">
                    {busy === "map" ? <Spinner /> : <><Wand2 size={12} /> Auto mapear</>}
                  </button>
                </div>
                <p className={`text-[11px] font-mono2 ${mappedCount === selGroups.length ? "text-emerald-300" : "text-amber-300"}`} data-testid="map-stats">
                  {mappedCount}/{selGroups.length} mapeadas ({selGroups.length ? Math.round(100 * mappedCount / selGroups.length) : 0}%)
                  {mapStats && ` · último auto-map: ${mapStats.matched} nas suas pastas, ${mapStats.new} novas propostas`}
                  {mappedCount < selGroups.length && " — as não mapeadas criam pasta pela categoria da lista"}
                </p>
                <MapTable cats={selGroups} mapping={mapping} setMapping={setMapping} folders={folders} prefix="create" kinds={effKinds()} folderKinds={folderKinds}
                  onRemove={(g) => { const s = { ...sel }; delete s[g]; setSel(s); const m = { ...mapping }; delete m[g]; setMapping(m); }} />
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <button data-testid="cat-import-now" onClick={importNow} disabled={!selGroups.length || !!busy}
                className="btn-neon flex-1 py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40">
                {busy === "now" ? <Spinner /> : <><Play size={15} /> Importar agora ({selItems.toLocaleString("pt-PT")} itens)</>}
              </button>
              <button data-testid="cat-schedule-open" onClick={() => setSchedOpen(!schedOpen)} disabled={!selGroups.length}
                className="btn-ghost-dark flex-1 py-3 rounded-xl text-sm flex items-center justify-center gap-2 text-amber-300 disabled:opacity-40">
                <CalendarClock size={15} /> Agendar…
              </button>
            </div>

            {schedOpen && (
              <div className="rounded-xl border border-amber-400/25 bg-amber-400/5 p-4 space-y-3" data-testid="sched-form">
                <p className="text-[10px] font-mono2 uppercase tracking-widest text-amber-300">Agendar importação destas {selGroups.length} categoria(s){mappedCount ? ` · ${mappedCount} mapeadas` : ""}</p>
                <input data-testid="sched-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nome (opcional — ex.: Lançamentos semanais)" className="input-dark w-full rounded-lg px-3 py-2.5 text-sm" />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <select data-testid="sched-freq" value={form.freq} onChange={(e) => setForm({ ...form, freq: e.target.value })} className="input-dark rounded-lg px-3 py-2.5 text-sm">
                    <option value="weekly">Semanal</option>
                    <option value="daily">Diário</option>
                  </select>
                  {form.freq === "weekly" && (
                    <select data-testid="sched-weekday" value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })} className="input-dark rounded-lg px-3 py-2.5 text-sm">
                      {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                    </select>
                  )}
                  <input data-testid="sched-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="input-dark rounded-lg px-3 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1">Pasta padrão (só para categorias não mapeadas)</label>
                  <select data-testid="sched-dest" value={form.dest} onChange={(e) => setForm({ ...form, dest: e.target.value })} className="input-dark w-full rounded-lg px-3 py-2.5 text-sm">
                    <option value="">Automática — cria/usa a pasta pela categoria da lista</option>
                    {folders.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <button data-testid="sched-create" onClick={createSchedule} disabled={!!busy} className="btn-neon w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2">
                  {busy === "sched" ? <Spinner /> : <><CalendarClock size={15} /> Criar agendamento</>}
                </button>
              </div>
            )}
          </div>
        )}
      </GlassCard>

      <GlassCard className="p-6" data-testid="sched-list-card">
        <h3 className="font-display text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <CalendarClock size={15} className="text-cyan-400" /> Agendamentos ativos
        </h3>
        {!schedules ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : schedules.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8" data-testid="sched-empty">Nenhuma importação agendada — leia uma lista acima, escolha e mapeie as categorias e marque o horário.</p>
        ) : (
          <div className="space-y-3" data-testid="sched-list">
            {schedules.map((s) => (
              <div key={s.id} className={`rounded-xl border p-4 space-y-2 ${s.active ? "border-slate-800" : "border-slate-800/50 opacity-60"}`} data-testid={`sched-item-${s.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${s.running ? "bg-cyan-400 animate-pulse" : s.active ? "bg-emerald-400" : "bg-slate-600"}`} />
                  <p className="text-sm text-white font-medium flex-1 min-w-0 truncate" title={s.url}>{s.name}</p>
                  <span className="text-[9px] font-mono2 uppercase tracking-widest px-2 py-0.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-300 shrink-0" data-testid={`sched-freq-${s.id}`}>{schedText(s)}</span>
                </div>
                {s.url && (
                  <p className="text-[10px] font-mono2 text-slate-500 truncate" title={s.url} data-testid={`sched-url-${s.id}`}>
                    <span className="text-slate-600">fonte: </span>{s.url}
                  </p>
                )}
                <div className="flex flex-wrap gap-1">
                  {(s.groups || ["<lista inteira>"]).slice(0, 4).map((g) => (
                    <span key={g} className="text-[9px] font-mono2 px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 truncate max-w-[160px]">{g}</span>
                  ))}
                  {(s.groups || []).length > 4 && <span className="text-[9px] font-mono2 text-slate-500">+{s.groups.length - 4}</span>}
                  {s.mapping && Object.keys(s.mapping).length > 0 && (
                    <span className="text-[9px] font-mono2 px-1.5 py-0.5 rounded bg-amber-400/10 border border-amber-400/25 text-amber-300" data-testid={`sched-mapped-${s.id}`}>
                      🗂 {Object.keys(s.mapping).length}/{s.groups?.length || 0} mapeadas
                    </span>
                  )}
                  {s.dest_folder && !s.mapping && <span className="text-[9px] font-mono2 px-1.5 py-0.5 rounded bg-amber-400/10 border border-amber-400/25 text-amber-300" data-testid={`sched-dest-${s.id}`}>→ 📂 {s.dest_folder}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] font-mono2 text-slate-500">
                  <span className="flex items-center gap-1"><Clock size={10} /> próxima: <b className="text-slate-300">{fmtDT(s.next_run)}</b></span>
                  {s.last_run && (
                    <span data-testid={`sched-last-${s.id}`}>última: {fmtDT(s.last_run)} · {s.last_status === "ok" ? <b className="text-emerald-300">ok ({s.last_total} itens)</b> : <b className="text-rose-300" title={s.last_error || ""}>erro</b>}</span>
                  )}
                  {s.running && <span className="text-cyan-300 animate-pulse">a correr agora…</span>}
                </div>
                {(s.runs || []).length > 0 && (
                  <button type="button" data-testid={`sched-hist-${s.id}`} onClick={() => setHistOpen({ ...histOpen, [s.id]: !histOpen[s.id] })} className="text-[10px] text-slate-500 hover:text-cyan-300 flex items-center gap-1 transition-colors">
                    {histOpen[s.id] ? <ChevronUp size={11} /> : <ChevronDown size={11} />} histórico ({s.runs.length})
                  </button>
                )}
                {histOpen[s.id] && (
                  <div className="space-y-1 pl-2 border-l border-slate-800" data-testid={`sched-hist-list-${s.id}`}>
                    {[...s.runs].reverse().map((r, i) => (
                      <p key={i} className="text-[10px] font-mono2 text-slate-500">{fmtDT(r.at)} — {r.status === "ok" ? <span className="text-emerald-300">ok · {r.total} itens</span> : <span className="text-rose-300">erro: {(r.error || "").slice(0, 80)}</span>}</p>
                    ))}
                  </div>
                )}
                <div className="flex gap-1.5 pt-1">
                  <button data-testid={`sched-edit-${s.id}`} onClick={() => openEdit(s)} title="Editar (horário, categorias, mapeamento)" className="p-2 rounded-lg btn-ghost-dark text-amber-300"><Pencil size={13} /></button>
                  <button data-testid={`sched-toggle-${s.id}`} onClick={() => toggle(s)} title={s.active ? "Pausar" : "Ativar"} className={`p-2 rounded-lg btn-ghost-dark ${s.active ? "text-emerald-300" : "text-slate-500"}`}><Power size={13} /></button>
                  <button data-testid={`sched-run-${s.id}`} onClick={() => runNow(s)} disabled={busy === `run-${s.id}` || s.running} title="Executar agora" className="p-2 rounded-lg btn-ghost-dark text-cyan-300 disabled:opacity-40">{busy === `run-${s.id}` ? <Spinner /> : <RefreshCw size={13} />}</button>
                  <button data-testid={`sched-del-${s.id}`} onClick={() => del(s)} disabled={busy === `del-${s.id}`} title="Apagar" className="p-2 rounded-lg btn-ghost-dark text-rose-400"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Editar agendamento — ${edit?.name || ""}`} testid="sched-edit-modal">
        {editForm && (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1">URL da lista (fonte deste agendamento)</label>
              <input data-testid="edit-url" value={edit?.url || ""} readOnly onFocus={(e) => e.target.select()}
                className="input-dark w-full rounded-lg px-3 py-2.5 text-[11px] font-mono2 opacity-70 cursor-text" />
            </div>
            <input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="input-dark w-full rounded-lg px-3 py-2.5 text-sm" placeholder="Nome" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <select data-testid="edit-freq" value={editForm.freq} onChange={(e) => setEditForm({ ...editForm, freq: e.target.value })} className="input-dark rounded-lg px-3 py-2.5 text-sm">
                <option value="weekly">Semanal</option>
                <option value="daily">Diário</option>
              </select>
              {editForm.freq === "weekly" && (
                <select data-testid="edit-weekday" value={editForm.weekday} onChange={(e) => setEditForm({ ...editForm, weekday: Number(e.target.value) })} className="input-dark rounded-lg px-3 py-2.5 text-sm">
                  {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              )}
              <input data-testid="edit-time" type="time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} className="input-dark rounded-lg px-3 py-2.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1">Pasta padrão (categorias não mapeadas)</label>
              <select data-testid="edit-dest" value={editForm.dest} onChange={(e) => setEditForm({ ...editForm, dest: e.target.value })} className="input-dark w-full rounded-lg px-3 py-2.5 text-sm">
                <option value="">Automática (pela categoria da lista)</option>
                {folders.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            {(edit?.groups || []).length > 0 && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 flex-1">Mapeamento categoria → pasta</p>
                  <button data-testid="edit-map-auto" onClick={() => autoMap(edit.groups, null, "edit")} disabled={!!busy} className="btn-ghost-dark px-2.5 py-1.5 rounded-lg text-[10px] flex items-center gap-1 text-cyan-300">
                    {busy === "map-edit" ? <Spinner /> : <><Wand2 size={11} /> Auto mapear</>}
                  </button>
                </div>
                <MapTable cats={edit.groups} mapping={editForm.mapping} setMapping={(m) => setEditForm({ ...editForm, mapping: m })} folders={folders} prefix="edit" kinds={Object.fromEntries((edit.group_kinds || []).map((g) => [g.name, g.series > g.movie ? "tv" : "movie"]))} folderKinds={folderKinds}
                  onRemove={(g) => { const gr = edit.groups.filter((x) => x !== g); const gk = (edit.group_kinds || []).filter((x) => x.name !== g); setEdit({ ...edit, groups: gr, group_kinds: gk }); const m = { ...editForm.mapping }; delete m[g]; setEditForm({ ...editForm, mapping: m }); }} />
              </div>
            )}
            <button data-testid="edit-save" onClick={saveEdit} disabled={!!busy} className="btn-neon w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2">
              {busy === "edit" ? <Spinner /> : <><Pencil size={14} /> Guardar alterações</>}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
